/**
 * PackingCalculator.js
 * Logic to calculate shipping dimensions and weights for ALL products.
 */

export const PackingCalculator = {

    /**
     * Main Calculation Function
     */
    calculate: function(prodId, opsId, width, height, dbConfig = null) {
        // Normalize inputs
        const pId = parseInt(prodId);
        const oId = parseInt(opsId);
        const w = parseFloat(width); // in Meters
        const h = parseFloat(height); // in Meters

        // --- 1. ROLLER SHADES (Prod 493 & 1945) ---
        if ([493, 1945].includes(pId)) {
            return this.calculateRollerBlind(pId, oId, w, h, dbConfig?.roller_config);
        }

        // --- 2. DUETTE / HONEYCOMB (Prod 533 & 2238) ---
        if ([533, 2238].includes(pId)) {
            return this.calculateDuette(pId, oId, w, h, dbConfig?.duette_config);
        }

        // --- 3. WOOD BLINDS (Prod 157 & 2268) ---
        if ([157, 2268].includes(pId)) {
            return this.calculateWoodBlind(pId, oId, w, h, dbConfig?.wood_config);
        }

        // --- 4. SILHOUETTE (Prod 76 & 2235) ---
        if ([76, 2235].includes(pId)) {
            return this.calculateSilhouette(pId, oId, w, h, dbConfig?.silhouette_config);
        }

        // --- 5. PIROUETTE (Prod 2236) ---
        if ([2236].includes(pId)) {
            return this.calculatePirouette(pId, oId, w, h, dbConfig?.pirouette_config);
        }

        // --- 6. FST SKYLIGHT (INTERIOR) (Prod 305 & 306) ---
        if ([305, 306].includes(pId)) {
            return this.calculateFST(pId, oId, w, h, dbConfig?.fst_config);
        }

        // --- 7. EXTERNAL ROLLER (EXTERIOR) (Prod 2635) ---
        if ([2635].includes(pId)) {
            return this.calculateExternalRoller(pId, oId, w, h, dbConfig?.ext_roller_config);
        }

        // --- 8. VENETIAN BLINDS (Prod 1323) ---
        if ([1323].includes(pId)) {
            return this.calculateVenetianBlind(pId, oId, w, h, dbConfig?.venetian_config);
        }

        // --- 9. EXTERIOR VENETIAN BLINDS (Prod 213) ---
        if ([213].includes(pId)) {
            return this.calculateExteriorVenetian(pId, oId, w, h, dbConfig?.ext_venetian_config);
        }

        // --- GLOBAL FALLBACK (Unknown Product) ---
        const { weight, source } = this.getGlobalWeight(w, dbConfig?.global_config);
        
        return [{
            error: "Unknown Product - Using Global Guide",
            description: `General Blind Box`,
            length: Math.ceil((w * 1000) + 130),
            width: 150,
            height: 150,
            weight: weight,
            source: source
        }];
    },

    /**
     * Helper: Global Weight Lookup
     */
    getGlobalWeight: function(widthM, config) {
        const defaultChart = [
            { w: 1.2, kg: 7 }, { w: 1.8, kg: 10 }, { w: 2.1, kg: 11 },
            { w: 2.55, kg: 13 }, { w: 2.95, kg: 15 }, { w: 3.3, kg: 17 }
        ];
        let chart = (config && config.weightChart) ? config.weightChart : defaultChart;
        let source = (config && config.weightChart) ? "DB" : "Hardcoded";

        let selectedKg = chart[chart.length - 1].kg;
        for (let i = 0; i < chart.length; i++) {
            if (widthM <= chart[i].w) {
                selectedKg = chart[i].kg;
                break;
            }
        }
        return { weight: selectedKg, source: source };
    },

    // =========================================================================
    // PRODUCT SPECIFIC LOGIC
    // =========================================================================

    calculateRollerBlind: function(prodId, opsId, w, h, config) {
        const opsTube37 = [577, 1676, 1677, 1678];
        const opsTube50 = [588, 570, 1679];
        const opsTube62 = [585, 583, 1680];
        const opsMotorDC = [1677, 1678]; 
        const opsMotorAll = [570, 583, 1679, 1680, 1677, 1678];

        const PAD_BASE = 130;
        const PAD_DC_EXTRA = 100;

        let lengthBuffer = PAD_BASE;
        if (opsMotorDC.includes(opsId)) {
            lengthBuffer += PAD_DC_EXTRA;
        }
        const boxL = Math.ceil((w * 1000) + lengthBuffer);

        let tubeKgPerM = 0.12; 
        let boxW = 110;
        let boxH = 110;

        if (opsTube50.includes(opsId)) {
            tubeKgPerM = 0.17;
            boxW = 110; boxH = 110;
        } else if (opsTube62.includes(opsId)) {
            tubeKgPerM = 0.26;
            boxW = 150; boxH = 150;
        }

        const FABRIC_KG_SQM = 0.53; 
        const TARE_KG_PER_M = 0.5;  
        const MOTOR_KG = 1.5;       

        let fixedKg = 0.5; 
        if (opsMotorAll.includes(opsId)) {
            fixedKg += MOTOR_KG;
        }

        const weight = fixedKg + (tubeKgPerM * w) + (TARE_KG_PER_M * w) + (FABRIC_KG_SQM * w * h);

        return [{ 
            description: "Roller Blind Box", 
            length: boxL, 
            width: boxW, 
            height: boxH, 
            weight: parseFloat(weight.toFixed(2)), 
            source: "Calculated" 
        }];
    },

calculateDuette: function(prodId, opsId, w, h, config) {
        const opsMotorDC = [79, 539, 543, 544, 1817];
        const isMotorized = opsMotorDC.includes(opsId);

        const PAD_BASE = 130;
        const PAD_DC_EXTRA = 100;
        let lengthBuffer = PAD_BASE + (isMotorized ? PAD_DC_EXTRA : 0);

        const boxL = Math.ceil((w * 1000) + lengthBuffer);
        const stackHeight = Math.ceil(80 + (35 * h)); 
        const boxH = Math.max(140, stackHeight); 

        // CALIBRATED FACTORS (Based on HD US Motorized Actuals)
        const defaults = { 
            factors: { 
                fixedKg: isMotorized ? 1.5 : 1.0, 
                railPerM: isMotorized ? 1.8 : 0.5, 
                fabPerSqm: isMotorized ? 3.2 : 1.1 
            } 
        };
        const f = (config && config.factors) ? config.factors : defaults.factors;

        const duWeight = f.fixedKg + (f.railPerM * w) + (f.fabPerSqm * w * h);

        return [{ 
            description: "Duette Box", 
            length: boxL, 
            width: 200, 
            height: boxH, 
            weight: parseFloat(duWeight.toFixed(2)), 
            source: "Calculated"
        }];
    },

    calculateWoodBlind: function(prodId, opsId, w, h, config) {
        const defaults = {
            stackTable: [
                { h: 500, stack: 135 }, { h: 1000, stack: 180 }, 
                { h: 1500, stack: 225 }, { h: 2000, stack: 265 }, 
                { h: 2500, stack: 310 }, { h: 3000, stack: 355 }
            ],
            factors: { fixedKg: 2.0, weightPerSqm: 3.5, tarePerM: 0.5 }
        };
        const useDB = config && config.factors;
        const cfg = useDB ? config : defaults;
        const f = cfg.factors;
        const stackTable = cfg.stackTable || defaults.stackTable;

        let boxH = 355; 
        const h_mm = h * 1000;
        let found = false;
        for (let i = 0; i < stackTable.length; i++) {
            if (h_mm <= stackTable[i].h) { 
                boxH = stackTable[i].stack; 
                found = true;
                break; 
            }
        }
        if (!found) {
            const maxEntry = stackTable[stackTable.length - 1];
            if (maxEntry && h_mm > maxEntry.h) {
                const extraSteps = Math.ceil((h_mm - maxEntry.h) / 500);
                boxH = maxEntry.stack + (extraSteps * 45);
            }
        }

        const boxL = Math.ceil((w * 1000) + 130);
        const woodWeight = f.fixedKg + (f.weightPerSqm * w * h) + (f.tarePerM * w);
        
        return [{ 
            description: "Wood Blind Box", 
            length: boxL, 
            width: 100, 
            height: boxH, 
            weight: parseFloat(woodWeight.toFixed(2)), 
            source: "Calculated"
        }];
    },

    calculateSilhouette: function(prodId, opsId, w, h, config) {
        const opsHeadrailLarge = [2469, 2470, 2471];
        const opsMotorDC = [79, 2471, 543];

        const PAD_BASE = 130;
        const PAD_DC_EXTRA = 100;

        let lengthBuffer = PAD_BASE;
        if (opsMotorDC.includes(opsId)) {
            lengthBuffer += PAD_DC_EXTRA;
        }
        const boxL = Math.ceil((w * 1000) + lengthBuffer);

        let boxW = 240;
        let boxH = 140;
        if (opsHeadrailLarge.includes(opsId)) {
            boxH = 200; 
        }

        const defaults = { factors: { fixedKg: 1.0, railPerM: 0.5, fabPerSqm: 3.0 } };
        const f = (config && config.factors) ? config.factors : defaults.factors;

        const silWeight = f.fixedKg + (f.railPerM * w) + (f.fabPerSqm * w * h);

        return [{ 
            description: "Silhouette Box", 
            length: boxL, 
            width: boxW, 
            height: boxH, 
            weight: parseFloat(silWeight.toFixed(2)), 
            source: "Calculated" 
        }];
    },

    calculatePirouette: function(prodId, opsId, w, h, config) {
        const opsMotorDC = [79, 643];
        const PAD_BASE = 130;
        const PAD_DC_EXTRA = 100;

        let lengthBuffer = PAD_BASE;
        if (opsMotorDC.includes(opsId)) {
            lengthBuffer += PAD_DC_EXTRA;
        }
        const boxL = Math.ceil((w * 1000) + lengthBuffer);
        const boxW = 145;
        const boxH = 145;

        const defaults = { factors: { fixedKg: 1.0, railPerM: 0.5, fabPerSqm: 2.7 } };
        const f = (config && config.factors) ? config.factors : defaults.factors;

        const pirWeight = f.fixedKg + (f.railPerM * w) + (f.fabPerSqm * w * h);

        return [{ 
            description: "Pirouette Box", 
            length: boxL, 
            width: boxW, 
            height: boxH, 
            weight: parseFloat(pirWeight.toFixed(2)), 
            source: "Calculated" 
        }];
    },

    calculateFST: function(prodId, opsId, w, h, config) {
        const isDualMotor = (prodId === 306);
        const boxL = Math.ceil((w * 1000) + 130);
        const boxW = 200;
        const boxH = 200;

        const FABRIC_KG_SQM = 0.58;
        const TUBE_KG_PER_M = 0.8; 
        const TARE_KG_PER_M = 1.0; 

        let fixedKg = 2.5; 
        if (isDualMotor) {
             fixedKg += 6.0; 
        } else {
             fixedKg += 3.0; 
        }

        const weight = fixedKg + (TUBE_KG_PER_M * w) + (TARE_KG_PER_M * w) + (FABRIC_KG_SQM * w * h);

        return [{ 
            description: isDualMotor ? "FST Dual Motor Box" : "FST Single Motor Box", 
            length: boxL, 
            width: boxW, 
            height: boxH, 
            weight: parseFloat(weight.toFixed(2)), 
            source: "Calculated" 
        }];
    },

    calculateExternalRoller: function(prodId, opsId, w, h, config) {
        const boxL = Math.ceil((w * 1000) + 130);
        const boxW = 200;
        const boxH = 200;

        const TUBE_KG_PER_M = 0.26;
        const FABRIC_KG_SQM = 0.6;
        const FIXED_KG = 4.5; 
        const HEADBOX_KG_PER_M = 4.0; 
        const SIDERAIL_KG_PER_M = 1.5; 

        const weight = FIXED_KG +
                       (TUBE_KG_PER_M * w) +
                       (HEADBOX_KG_PER_M * w) +
                       (SIDERAIL_KG_PER_M * h) + 
                       (FABRIC_KG_SQM * w * h);

        return [{ 
            description: "External Roller (Box + Rails)", 
            length: boxL, 
            width: boxW, 
            height: boxH, 
            weight: parseFloat(weight.toFixed(2)), 
            source: "Calculated" 
        }];
    },

    calculateVenetianBlind: function(prodId, opsId, w, h, config) {
        const boxL = Math.ceil((w * 1000) + 130);
        const h_mm = h * 1000;
        const numSlats = h_mm / 21;
        const stackMm = 55 + (numSlats * 0.25);
        
        const boxH = Math.max(120, Math.ceil(stackMm + 20)); 
        const boxW = 120; 

        const defaults = { factors: { fixedKg: 2.0, railPerM: 0.5, fabPerSqm: 2.0 } };
        const f = (config && config.factors) ? config.factors : defaults.factors;

        const venWeight = f.fixedKg + (f.railPerM * w) + (f.fabPerSqm * w * h);

        return [{ 
            description: "Venetian Blind Box", 
            length: boxL, 
            width: boxW, 
            height: boxH, 
            weight: parseFloat(venWeight.toFixed(2)), 
            source: "Calculated" 
        }];
    },

    calculateExteriorVenetian: function(prodId, opsId, w, h, config) {
        // --- 1. Ops Logic (Dual vs Single) ---
        // Dual: 216, 217, 218, 219 (Shared Motor/Crank, Heavy Headrail)
        // Single: 2183, 2184, 2485, 2186
        const opsDual = [216, 217, 218, 219];
        const isDual = opsDual.includes(opsId);

        // --- 2. Length Logic ---
        // Length = Max(Width, Height) + 200mm Buffer (Accommodate Side Rails if H > W)
        // Note: For unit packing, we usually ensure the box fits the longest component.
        const longestDim = Math.max(w, h);
        const boxL = Math.ceil((longestDim * 1000) + 200);

        // --- 3. Stack/Height Logic ---
        // Formula: 100mm Base + 80mm per meter height
        const stackHeight = Math.ceil(100 + (80 * h)); 
        const boxH = Math.max(250, stackHeight + 50); // Min 250 for motor clearance
        const boxW = 250; // Heavy duty profile

        // --- 4. Weight Logic ---
        // Calibrated to 33kg for 2.7x3.5m unit
        const MOTOR_KG = 2.3;
        const CRANK_KG = 3.0;
        
        // Manual Ops: 216, 2183
        const opsManual = [216, 2183];
        const fixedKg = opsManual.includes(opsId) ? CRANK_KG : MOTOR_KG;

        // Multipliers
        // Headrail: 2.5 kg/m (Single) -> 5.0 kg/m (Dual)
        // Slats: 2.5 kg/m2 (Heavy duty aluminum + tapes)
        const railPerM = isDual ? 5.0 : 2.5;
        const fabPerSqm = 2.5;

        const weight = fixedKg + 
                       (railPerM * w) + 
                       (fabPerSqm * w * h);

        return [{ 
            description: isDual ? "Ext Venetian (Dual/Shared)" : "Ext Venetian (Single)", 
            length: boxL, 
            width: boxW, 
            height: boxH, 
            weight: parseFloat(weight.toFixed(2)), 
            source: "Calculated" 
        }];
    }
};